import { isGridDefault, NetworkEnum } from "@platform/plan-grid-core";

import type { SbsCellStateMap } from "../components/PlanEditor/context/SbsCellStateContext";
import type { SbsStatusType } from "@platform/plan-grid-core";

type GridBenefit = Parameters<typeof isGridDefault>[1];

/** A grid row as the Quotes grid holds it: one value per plan column id. */
export interface ComparableRow {
  benefitKey: string;
  benefit?: GridBenefit;
  /** Companion fields the grid folded into this row, by field then by plan id. */
  __collapsedSiblings?: unknown;
  [planId: string]: unknown;
}

interface ComparablePlanRef {
  id: string;
  position?: number;
}

/** The slice of a carrier column group the pairing needs. */
export interface ComparableCarrierGroup {
  carrierId: string;
  benefitPlanGroups: readonly {
    benefitPlans: readonly ComparablePlanRef[];
  }[];
}

interface BuildDifferenceCellStateParams {
  rows: readonly ComparableRow[];
  /** Each quoted plan id mapped to the requested plan id it answers. */
  pairs: ReadonlyMap<string, string>;
  /**
   * The quoted value an analyst dismissed this cell's flag at, as the
   * `differenceValue` the flag carried. `undefined` when the cell was never
   * dismissed. `null` for a dismissal that stored no value, which stays
   * dismissed whatever the cell holds.
   */
  dismissedValueOf: (
    planId: string,
    benefitKey: string,
  ) => string | null | undefined;
  /**
   * Whether the analyst's custom text is the active value of a quoted cell
   * (`network` is set for a network column). Free text is never "the same" as a
   * request, so such a cell is not flagged.
   */
  hasCustomText?: (
    planId: string,
    benefitKey: string,
    network?: string,
  ) => boolean;
}

/**
 * Fields that take open text. A free-text answer is never "the same" in a way
 * a comparison can judge, so these are never flagged. `rateGuaranteeCap` is the
 * cap folded into the rate guarantee row: only the cap is skipped, so a
 * differing rate guarantee still flags.
 */
const OPEN_TEXT_FIELDS: ReadonlySet<string> = new Set([
  "planName",
  "networkName",
  "rateGuaranteeCap",
  "historyAndTakeover",
  "companyLeaves",
  "correspondence",
  "ridersIncluded",
  "additionalOptionsIncluded",
  "perOccurrencePerYear",
  "legalFinancialResources",
]);

const DIFFERENCE_STATUS: SbsStatusType = "difference";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const NUMERIC_STRING = /^-?\d+(\.\d+)?$/;

/**
 * Reduces a value to a comparable form: numeric strings become numbers (the
 * request side can hold "500" where a quote holds 500) and object keys are
 * sorted so key order never reads as a difference. Array order is preserved
 * because it is meaningful for schedules.
 */
const canonicalize = (value: unknown): unknown => {
  if (typeof value === "string" && NUMERIC_STRING.test(value.trim())) {
    return Number(value);
  }
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, canonicalize(value[key])]),
    );
  }

  return value;
};

/**
 * The part of a cell value that says something: undefined when the cell is
 * empty or holds only the grid's seeded default, and for a network value
 * (`{ inNetwork, outOfNetwork }`) only the networks that hold a real value.
 */
const meaningful = (value: unknown, benefit: GridBenefit): unknown => {
  if (isPlainObject(value)) {
    const present = Object.entries(value)
      .map(([key, entry]) => [key, meaningful(entry, benefit)] as const)
      .filter(([, entry]) => entry !== undefined);

    return present.length > 0 ? Object.fromEntries(present) : undefined;
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value : undefined;
  }

  return isGridDefault(value, benefit) ? undefined : value;
};

/**
 * Whether the quoted value departs from the requested one. Network values are
 * compared network by network, and a network only one side holds is a
 * difference.
 */
const valuesDiffer = (requested: unknown, quoted: unknown): boolean => {
  if (isPlainObject(requested) && isPlainObject(quoted)) {
    return [
      ...new Set([...Object.keys(requested), ...Object.keys(quoted)]),
    ].some((key) => valuesDiffer(requested[key], quoted[key]));
  }

  return (
    JSON.stringify(canonicalize(requested)) !==
    JSON.stringify(canonicalize(quoted))
  );
};

/**
 * What a cell shows for one plan. The grid folds a benefit's companion fields
 * into one displayed row (a type row carrying its dollar amount, say) and keeps
 * the folded values under `__collapsedSiblings`, so the row's own value alone
 * would miss a difference in the amount. A sibling under the row's own key is
 * a copy of the row's value (the editor writes one for the edited column
 * only), so it is skipped: counted, it would read as a difference against the
 * column that has no copy.
 */
const cellValue = (gridRow: ComparableRow, planId: string): unknown => {
  const siblings = isPlainObject(gridRow.__collapsedSiblings)
    ? gridRow.__collapsedSiblings
    : {};

  return {
    ...Object.fromEntries(
      Object.entries(siblings)
        .filter(
          ([field]) =>
            !OPEN_TEXT_FIELDS.has(field) && field !== gridRow.benefitKey,
        )
        .map(([field, byPlan]) => [
          field,
          isPlainObject(byPlan) ? byPlan[planId] : undefined,
        ]),
    ),
    __value: gridRow[planId],
  };
};

const NETWORK_KEYS: readonly string[] = [
  NetworkEnum.IN_NETWORK,
  NetworkEnum.OUT_OF_NETWORK,
];

const isNetworkValue = (value: unknown): value is Record<string, unknown> =>
  isPlainObject(value) &&
  Object.keys(value).some((k) => NETWORK_KEYS.includes(k));

/**
 * The networks a cell value holds, looking through the companion fields the
 * grid folded in beside the row's own value. Empty for a value with no
 * network split.
 */
const networksIn = (value: unknown): string[] => {
  if (isNetworkValue(value)) {
    return Object.keys(value).filter((k) => NETWORK_KEYS.includes(k));
  }

  return isPlainObject(value) ? Object.values(value).flatMap(networksIn) : [];
};

/** The cell value narrowed to one network, wherever it holds a network split. */
const forNetwork = (value: unknown, network: string): unknown => {
  if (isNetworkValue(value)) {
    return value[network];
  }

  return isPlainObject(value)
    ? Object.fromEntries(
        Object.entries(value).map(([key, entry]) => [
          key,
          forNetwork(entry, network),
        ]),
      )
    : value;
};

/** 1-based slot of a plan within its group; the array index when no position is set. */
const slotOf = (plan: ComparablePlanRef, index: number): number =>
  plan.position ?? index + 1;

/**
 * Pairs each quoted plan with the requested plan it answers: the same slot
 * (`position`, else array index) of the page's single requested plan group.
 * With more than one requested plan group on the page (the Unmapped Plans page)
 * the pairing is ambiguous, so nothing is paired.
 */
export const pairQuotedWithRequestedPlans = (
  carrierGroups: readonly ComparableCarrierGroup[],
  requestedCarrierId: string,
): ReadonlyMap<string, string> => {
  const requestedGroups = carrierGroups
    .filter((carrier) => carrier.carrierId === requestedCarrierId)
    .flatMap((carrier) => carrier.benefitPlanGroups);

  if (requestedGroups.length !== 1) {
    return new Map();
  }

  const requestedBySlot = new Map(
    requestedGroups[0].benefitPlans.map((plan, index) => [
      slotOf(plan, index),
      plan.id,
    ]),
  );

  return new Map(
    carrierGroups
      .filter((carrier) => carrier.carrierId !== requestedCarrierId)
      .flatMap((carrier) => carrier.benefitPlanGroups)
      .flatMap((quotedGroup) =>
        quotedGroup.benefitPlans.flatMap((quoted, index) => {
          const requestedId = requestedBySlot.get(slotOf(quoted, index));

          return requestedId === undefined
            ? []
            : [[quoted.id, requestedId] as const];
        }),
      ),
  );
};

/** A cell value as the text a dismissal stores and later compares against. */
const serializeCellValue = (value: unknown): string =>
  JSON.stringify(canonicalize(value)) ?? "null";

/**
 * Compares each quoted cell with the requested cell on the same grid row and
 * returns a cell state map flagging those that differ, for the "Different Than
 * Requested" flag. It works on the grid's own row values, the ones the analyst
 * sees, because a row merges several stored fields under one benefit key. An
 * empty cell counts as a value: a value requested but not quoted, or quoted but
 * not requested, is a difference. Open-text fields are never flagged. Each flag
 * carries the quoted value it was raised at, so a dismissal made at that value
 * stays dismissed until the quoted value changes.
 */
export const buildDifferenceCellState = ({
  rows,
  pairs,
  dismissedValueOf,
  hasCustomText = () => false,
}: BuildDifferenceCellStateParams): SbsCellStateMap =>
  rows
    .filter((gridRow) => !OPEN_TEXT_FIELDS.has(gridRow.benefitKey))
    .reduce<SbsCellStateMap>((state, gridRow) => {
      const flagged = [...pairs].flatMap(([quotedId, requestedId]) => {
        const requested = meaningful(
          cellValue(gridRow, requestedId),
          gridRow.benefit,
        );
        const quoted = meaningful(
          cellValue(gridRow, quotedId),
          gridRow.benefit,
        );
        const differenceValue = serializeCellValue(quoted);
        const dismissedAt = dismissedValueOf(quotedId, gridRow.benefitKey);
        const isDismissed =
          dismissedAt !== undefined &&
          (dismissedAt === null || dismissedAt === differenceValue);

        if (isDismissed) {
          return [];
        }

        // A network value flags per network column, named the way the grid
        // names the column (`planId.network`), so each network answers to its
        // own requested value.
        const networks = [
          ...new Set([...networksIn(requested), ...networksIn(quoted)]),
        ];

        return networks.length > 0
          ? networks
              .filter(
                (network) =>
                  valuesDiffer(
                    forNetwork(requested, network),
                    forNetwork(quoted, network),
                  ) && !hasCustomText(quotedId, gridRow.benefitKey, network),
              )
              .map(
                (network) =>
                  [`${quotedId}.${network}`, differenceValue] as const,
              )
          : valuesDiffer(requested, quoted) &&
              !hasCustomText(quotedId, gridRow.benefitKey)
            ? [[quotedId, differenceValue] as const]
            : [];
      });

      return flagged.length === 0
        ? state
        : {
            ...state,
            [gridRow.benefitKey]: Object.fromEntries(
              flagged.map(([fieldKey, differenceValue]) => [
                fieldKey,
                { statuses: [DIFFERENCE_STATUS], differenceValue },
              ]),
            ),
          };
    }, {});
